import { PatientSection } from './components/request-steps/patient/PatientSection';
import { RequestSection } from './components/request-steps/request/RequestSection';
import { ReleaseSection } from './components/request-steps/release/ReleaseSection';
import { SubmitSection } from './components/request-steps/submit/SubmitSection';
import { StepId, Gender } from './types';
import { RetrievalMethod } from '@/feature/profiles/constants';
import { DownloadMedicalCategory } from '@/feature/downloads/types';
import {
  PatientCreationState,
  PatientCreationStep_Patient,
  PatientCreationStep_Release,
  PatientCreationStep_Request,
  PatientCreationStep_Submit,
  PatientStatus,
} from './types';
import { ProviderFacilityFormData } from './components/request-steps/request/suggested-providers-facility/validation';
import {
  AlertTriangle,
  CheckCircle,
  CircleEllipsis,
  CircleFadingPlus,
  CircleOff,
  CircleX,
  ClipboardCheck,
  ClipboardX,
  FileCheck2,
  FileInput,
  FileMinus,
  FileSearch2,
  FileUp,
} from 'lucide-react';

export const PATIENT_REQUEST_STEPS: {
  id: StepId;
  title: string;
  component?: React.ComponentType;
}[] = [
  {
    id: 'patient',
    title: 'Patient',
    component: PatientSection,
  },
  {
    id: 'request',
    title: 'Request',
    component: RequestSection,
  },
  {
    id: 'release',
    title: 'Release',
    component: ReleaseSection,
  },
  {
    id: 'submit',
    title: 'Submit',
    component: SubmitSection,
  },
];

export const GENDER_OPTIONS: { label: string; value: Gender }[] = [
  { label: 'Male', value: Gender.MALE },
  { label: 'Female', value: Gender.FEMALE },
  { label: 'Other', value: Gender.OTHER },
  { label: 'Prefer not to say', value: Gender.PREFER_NOT_TO_SAY },
] as const;

export const defaultPatient: PatientCreationStep_Patient = {
  firstName: '',
  lastName: '',
  middleName: undefined,
  dateOfBirth: '',
  genderName: '' as Gender,
  contact: {
    phoneNumber: '',
    emailAddress: undefined,
  },
  address: {
    address1: undefined,
    address2: undefined,
    city: undefined,
    state: undefined,
    postalCode: '',
  },
};

const defaultRequest: PatientCreationStep_Request = {
  selectedRequestMethods: [],
  actionFormsData: {
    [RetrievalMethod.HIE]: {
      alternativePostalCodes: [],
    },
    [RetrievalMethod.FACESHEETS]: {
      selectedFacesheets: [],
    },
    [RetrievalMethod.MEDICATIONS]: {
      retrieveMedications: true,
    },
    [RetrievalMethod.PROVIDER_FACILITY]: {
      requestFrom: '',
      releasedTo: '',
      retrieveFrom: [],
    },
    [RetrievalMethod.RECORD_UPLOAD]: {
      uploadedFiles: [],
    },
  },
};

const defaultRelease: PatientCreationStep_Release = {};

const defaultSubmit: PatientCreationStep_Submit = {
  notificationEmails: [],
  referenceId: undefined,
  requestReason: 'upcoming-appointment',
  dateNeededBy: undefined,
};

export const formDefaultValues: PatientCreationState = {
  patient: defaultPatient,
  request: defaultRequest,
  release: defaultRelease,
  submit: defaultSubmit,
  organizationProfileId: '',
};

export const defaultProviderFacilityFormValues: Partial<ProviderFacilityFormData> =
  {
    providerName: '',
    facilityName: '',
    specialty: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    phoneNumber: '',
    faxNumber: '',
    recordsRequested: 'complete' as const,
    serviceDateRange: '6months' as const,
    serviceStartDate: '',
    serviceEndDate: '',
    medicalRecordDetails: '',
  };

// ============================================
// Medical Records Labels
// ============================================

export const COMPLETE_MEDICAL_RECORDS = 'Complete Medical Records';
export const SPECIFIC_MEDICAL_RECORDS = 'Specific Medical Records';

// ============================================
// Base Status Enums
// ============================================

export enum RetrievalStatus {
  Open = 'OPEN',
  Processing = 'PROCESSING',
  ActionRequired = 'ACTION REQUIRED',
  Completed = 'COMPLETED',
  Cancelled = 'CANCELLED',
  Closed = 'CLOSED',
}

export enum FacesheetRequestStatus {
  Pending = 'FS_PENDING',
  Processing = 'FS_PROCESSING',
  Completed = 'FS_COMPLETE',
  Closed = 'FS_CLOSED',
}

export enum RecordUploadRequestStatus {
  Open = 'RU_OPEN',
  Processing = 'RU_PROCESSING',
  Completed = 'RU_COMPLETED',
}

// Used by router/API for record upload status (different from patient display status)
export enum RecordUploadStatus {
  Open = 'OPEN',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export enum MedicationsRequestStatus {
  DataRetrieved = 'DATA_RETRIEVED',
  NoDataToRetrieve = 'NO_DATA_TO_RETRIEVE',
  Processing = 'PROCESSING',
  ResponseReceived = 'RESPONSE_RECEIVED',
  Failed = 'FAILED',
}

export enum HieRequestStatus {
  Created = 'Created',
  Processing = 'Processing',
  Failed = 'Failed',
  DataAvailable = 'HIE_data_available',
  DataNotAvailable = 'No_HIE_data_available',
}

// ============================================
// Patient Status Constants (using enums)
// ============================================

export const ALL_PATIENT_STATUSES: Array<{
  type: PatientStatus;
  count: number;
  label: string;
}> = [
  { type: RetrievalStatus.Open, count: 0, label: 'Retrieval Open' },
  { type: RetrievalStatus.Processing, count: 0, label: 'Retrieval Processing' },
  {
    type: RetrievalStatus.ActionRequired,
    count: 0,
    label: 'Retrieval Action Required',
  },
  { type: RetrievalStatus.Completed, count: 0, label: 'Retrieval Complete' },
  { type: RetrievalStatus.Cancelled, count: 0, label: 'Retrieval Canceled' },
  { type: RetrievalStatus.Closed, count: 0, label: 'Retrieval Closed' },
  {
    type: FacesheetRequestStatus.Pending,
    count: 0,
    label: 'Facesheet Pending',
  },
  {
    type: FacesheetRequestStatus.Processing,
    count: 0,
    label: 'Facesheet Processing',
  },
  { type: FacesheetRequestStatus.Closed, count: 0, label: 'Facesheet Closed' },
  {
    type: FacesheetRequestStatus.Completed,
    count: 0,
    label: 'Facesheet Complete',
  },
  {
    type: RecordUploadRequestStatus.Open,
    count: 0,
    label: 'Upload Open',
  },
  {
    type: RecordUploadRequestStatus.Processing,
    count: 0,
    label: 'Upload Processing',
  },
  {
    type: RecordUploadRequestStatus.Completed,
    count: 0,
    label: 'Upload Complete',
  },
];

// Combined status configuration with icon, color, and filter label
export const STATUS_CONFIG: Partial<
  Record<
    PatientStatus,
    {
      icon: React.ComponentType<{ className?: string }>;
      iconColor: string;
      filterLabel: string;
      textColor?: string;
    }
  >
> = {
  [RetrievalStatus.Open]: {
    icon: CircleEllipsis,
    iconColor: 'text-cyan-500',
    filterLabel: 'Open',
  },
  [RetrievalStatus.Processing]: {
    icon: CircleFadingPlus,
    iconColor: 'text-blue-400',
    filterLabel: 'Processing',
  },
  [RetrievalStatus.ActionRequired]: {
    icon: AlertTriangle,
    iconColor: 'text-amber-700',
    filterLabel: 'Action Required',
  },
  [RetrievalStatus.Completed]: {
    icon: CheckCircle,
    iconColor: 'text-green-400',
    filterLabel: 'Completed',
    textColor: 'text-green-400',
  },
  [RetrievalStatus.Cancelled]: {
    icon: CircleOff,
    iconColor: 'text-gray-400',
    filterLabel: 'Canceled',
  },
  [RetrievalStatus.Closed]: {
    icon: CircleX,
    iconColor: 'text-red-400',
    filterLabel: 'Closed',
    textColor: 'text-red-400',
  },
  [FacesheetRequestStatus.Pending]: {
    icon: FileMinus,
    iconColor: 'text-cyan-500',
    filterLabel: 'Pending',
  },
  [FacesheetRequestStatus.Processing]: {
    icon: FileSearch2,
    iconColor: 'text-blue-400',
    filterLabel: 'Processing',
  },
  [FacesheetRequestStatus.Completed]: {
    icon: ClipboardCheck,
    iconColor: 'text-green-400',
    filterLabel: 'Completed',
    textColor: 'text-green-400',
  },
  [FacesheetRequestStatus.Closed]: {
    icon: ClipboardX,
    iconColor: 'text-red-400',
    filterLabel: 'Closed',
    textColor: 'text-red-400',
  },
  [RecordUploadRequestStatus.Open]: {
    icon: FileUp,
    iconColor: 'text-cyan-500',
    filterLabel: 'Upload Open',
  },
  [RecordUploadRequestStatus.Processing]: {
    icon: FileInput,
    iconColor: 'text-blue-400',
    filterLabel: 'Processing',
  },
  [RecordUploadRequestStatus.Completed]: {
    icon: FileCheck2,
    iconColor: 'text-green-500',
    filterLabel: 'Completed',
    textColor: 'text-green-500',
  },
};

// ============================================
// Status Group Arrays (using enums)
// ============================================

export const OPEN_STATUSES: PatientStatus[] = [
  RetrievalStatus.Open,
  RecordUploadRequestStatus.Open,
];

export const PROCESSING_STATUSES: PatientStatus[] = [
  FacesheetRequestStatus.Pending,
  RetrievalStatus.Processing,
  FacesheetRequestStatus.Processing,
  RecordUploadRequestStatus.Processing,
  RetrievalStatus.ActionRequired,
];

export const CLOSED_STATUSES: PatientStatus[] = [
  RetrievalStatus.Closed,
  FacesheetRequestStatus.Closed,
];

export const CANCELED_STATUSES: PatientStatus[] = [RetrievalStatus.Cancelled];

export const COMPLETED_STATUSES: PatientStatus[] = [
  RetrievalStatus.Completed,
  FacesheetRequestStatus.Completed,
  RecordUploadRequestStatus.Completed,
];

// ─── Download Documents ──────────────────────────────────────────────────────

export const DOWNLOAD_CATEGORY_LABELS: Record<DownloadMedicalCategory, string> =
  {
    [DownloadMedicalCategory.AdditionalRecords]: 'Additional Records',
    [DownloadMedicalCategory.ImagingResult]: 'Imaging',
    [DownloadMedicalCategory.LabResult]: 'Lab Results',
    [DownloadMedicalCategory.Medications]: 'Medications',
    [DownloadMedicalCategory.PathologyResults]: 'Pathology Results',
    [DownloadMedicalCategory.ProcedureNote]: 'Procedures',
    [DownloadMedicalCategory.ProviderNote]: 'Provider/Facility Notes',
  };

export const DOWNLOAD_MEDICAL_CATEGORY_VALUES = new Set(
  Object.values(DownloadMedicalCategory),
);

export const DOWNLOAD_CATEGORY_ORDER: DownloadMedicalCategory[] = [
  DownloadMedicalCategory.ProviderNote,
  DownloadMedicalCategory.ImagingResult,
  DownloadMedicalCategory.ProcedureNote,
  DownloadMedicalCategory.LabResult,
  DownloadMedicalCategory.Medications,
  DownloadMedicalCategory.PathologyResults,
  DownloadMedicalCategory.AdditionalRecords,
];
